import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';

import ReactCountryFlag from 'react-country-flag';

import Question from './Question';
import { mislecAPI } from '../../../../util/axios';
import Iconify from '../../../../theme/iconify/iconify';
import { MaterialContext } from '../../../../context/MaterialContext';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
};

function QuizModal({ user, updateUser, open, setOpen, quiz, setQuiz, final, lng, courseId, sections, sectionId, saveQuiz, setNotification, setMessage, setSeverity }) {
    const [title, setTitle] = React.useState('');
    const [examQuestions, setExamQuestions] = React.useState('');
    const [timeLimit, setTimeLimit] = React.useState('');
    const [attempts, setAttempts] = React.useState('');
    const [noSingle, setNoSingle] = React.useState(final ? 3 : 1);
    const [noSingleError, setNoSingleError] = React.useState(false);
    const [noMultiple, setNoMultiple] = React.useState(final ? 3 : 1);
    const [noMultipleError, setNoMultipleError] = React.useState(false);
    const [language, setLanguage] = React.useState('');
    const { material, setMaterial } = React.useContext(MaterialContext);
    const [selectedSections, setSelectedSections] = React.useState([]);
    const [selectedMaterial, setSelectedMaterial] = React.useState([]);
    const [questions, setQuestions] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const { i18n, t } = useTranslation();

    React.useEffect(() => {
        if (questions) return;

        if (quiz) {
            setTitle(sections?.filter((section) => section.id === sectionId)[0]?.title);
            setQuestions(quiz.questions);
            if (examQuestions === '') setExamQuestions(quiz.no_questions);
            if (timeLimit === '') setTimeLimit(quiz.time_limit);
            if (attempts === '') setAttempts(quiz.no_attempts);
        }
    }, [quiz]);

    React.useEffect(() => {
        if (!courseId) return;
        setMaterial(null);
        setLanguage(lng);
    }, [courseId]);

    React.useEffect(() => {
        if (!open) return;
        setLanguage(lng);
        setNoSingle(final ? 3 : 1);
        setNoMultiple(final ? 3 : 1);
    }, [open]);

    React.useEffect(() => {
        if (material || courseId === undefined) return;

        mislecAPI.get(`/api/course_documents/${courseId}`)
            .then(res => {
                setMaterial(res.data.documents);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                setMaterial([]);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }, [material, courseId]);

    const generate = () => {
        setLoading(true);

        //  Data preparation
        const data = {
            section_ids: selectedSections.length > 0 ? selectedSections.map(section => section.id) : [sectionId],
            language: language,
            single: noSingle,
            multiple: noMultiple,
            documents: selectedMaterial?.map((material) => material._id)
        };

        //  Generate questions
        mislecAPI.post(`/api/course/${courseId}`, data)
            .then((response) => {
                setLoading(false);
                if (response.data.questions.length !== parseInt(noSingle) + parseInt(noMultiple)) warn(t('course.modals.newExam.questions.error.generate'));
                else setQuestions(response.data.questions);
            })
            .catch((error) => {
                setLoading(false);

                // Notify user of error
                setNotification(true);
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const handleUpdate = (index, question) => {
        const newQuestions = [...questions];
        newQuestions[index] = question;
        setQuestions(newQuestions);
    }

    const handleSave = () => {
        if (questions.length === 0) return;

        if (questions.some(question => question.answers.length === 0)) {
            warn(t('course.modals.newExam.error.noAnswers'));
            return;
        }

        if (final) {
            if (!title) {
                warn(t('course.modals.newExam.error.noTitle'));
                return;
            }
            if (!examQuestions) {
                warn(t('course.modals.newExam.error.noQuestions'));
                return;
            }
            if (!/^\d*$/.test(examQuestions)) {
                warn(t('course.modals.newExam.error.questions'));
                return;
            }
            if (examQuestions < questions?.filter(question => question.is_required).length) {
                warn(t('course.modals.newExam.error.required'));
                return;
            }
            if (examQuestions > questions?.length) {
                warn(t('course.modals.newExam.error.questionLimit'));
                return;
            }
            if (timeLimit && !/^\d*$/.test(timeLimit)) {
                warn(t('course.modals.newExam.error.time'));
                return;
            }
            if (!attempts) {
                warn(t('course.modals.newExam.error.noAttempts'));
                return;
            }
            if (!/^\d*$/.test(attempts)) {
                warn(t('course.modals.newExam.error.attempts'));
                return;
            }
        }

        const data = {
            id: quiz?.id ? quiz.id : null,
            type: final ? 'exam' : 'learn',
            questions: questions,
            no_questions: final ? parseInt(examQuestions) : null,
            time_limit: final && timeLimit ? parseInt(timeLimit) : null,
            no_attempts: final ? parseInt(attempts) : null
        };

        if (final) saveQuiz(data, title);
        else saveQuiz(data);

        //  Initialise values
        handleClose();
    }

    const warn = (message) => {
        setNotification(true);
        setMessage(message);
        setSeverity('warning');
    }

    const insertQuestion = (index, type) => {
        const newQuestions = [...questions];

        const single = [{ answer: '', is_correct: true }, { answer: '', is_correct: false }];
        const multiple = [{ answer: '', is_correct: true }, { answer: '', is_correct: false }, { answer: '', is_correct: false }, { answer: '', is_correct: false }];

        newQuestions.splice(index + 1, 0, {
            question: '',
            type: type,
            is_required: false,
            answers: type === 'singleChoice' ? single : multiple,
            temp_id: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
        });

        setQuestions(newQuestions);
    }

    const moveQuestion = (index, direction) => {
        const newQuestions = [...questions];

        if (direction === 'up') {
            const temp = newQuestions[index - 1];
            newQuestions[index - 1] = newQuestions[index];
            newQuestions[index] = temp;
        } else if (direction === 'down') {
            const temp = newQuestions[index + 1];
            newQuestions[index + 1] = newQuestions[index];
            newQuestions[index] = temp;
        } else if (direction === 'start') {
            const temp = newQuestions[index];
            newQuestions.splice(index, 1);
            newQuestions.unshift(temp);
        } else if (direction === 'end') {
            const temp = newQuestions[index];
            newQuestions.splice(index, 1);
            newQuestions.push(temp);
        }

        setQuestions(newQuestions);
    }

    const duplicateQuestion = (index) => {
        const newQuestions = [...questions];
        newQuestions.splice(index, 0, questions[index]);
        setQuestions(newQuestions);
    }

    const removeQuestion = (index) => {
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        setQuestions(newQuestions);
    }

    const handleNoSingleChange = (value) => {
        if (!/^\d*$/.test(value)) {
            setNoSingleError(true);
            return;
        } else {
            setNoSingleError(false);
        }

        setNoSingle(value);
    }

    const handleNoMultipleChange = (value) => {
        if (!/^\d*$/.test(value)) {
            setNoMultipleError(true);
            return;
        } else {
            setNoMultipleError(false);
        }

        setNoMultiple(value);
    }

    const handleClose = () => {
        setOpen(false);
        setQuestions(null);
        setQuiz(null);
        setNoSingle(1);
        setNoMultiple(1);
        setTitle('');
        setExamQuestions('');
        setTimeLimit('');
        setAttempts('');
        setLanguage('');
        setMaterial(null);
        setSelectedSections([]);
        setSelectedMaterial([]);
    }

    return (
        <Modal
            open={open}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    padding: '24px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    minWidth: { xs: '95%', md: '50%' },
                    maxWidth: { xs: '95%', md: '50%' },
                }}
            >
                <Typography variant='h6'>
                    {t(`course.modals.new${final ? 'Exam' : 'Quiz'}.${quiz ? 'edit' : 'title'}`)}
                </Typography>
                {final &&
                    <>
                        <Grid item xs={12} sx={{ my: 2 }}>
                            <Divider />
                        </Grid>
                        <Typography variant='h6' sx={{ fontSize: '1rem !important' }}>
                            {t('course.modals.newQuiz.settings.title')}
                        </Typography>
                        <Typography variant='body2' sx={{ mt: 1, mb: 2 }}>
                            {t(`course.modals.${final ? 'newExam' : 'newQuiz'}.settings.description`)}
                        </Typography>
                        <TextField
                            id='mISLec_course_quiz_title'
                            name='title'
                            size='small'
                            label={t('course.modals.newQuiz.settings.inputs.title.label')}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                        />
                        <Grid container spacing={2} sx={{ mt: 0.25 }}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id='mISLec_course_quiz_questions'
                                    label={t('course.modals.newExam.settings.inputs.questions.label')}
                                    name='title'
                                    size='small'
                                    disabled={questions?.length === 0}
                                    value={examQuestions}
                                    onChange={(e) => setExamQuestions(e.target.value)}
                                    error={examQuestions && !/^\d*$/.test(examQuestions)}
                                    helperText={examQuestions && !/^\d*$/.test(examQuestions) ? t('profile.tabs.profile.basic.inputs.helper.numeric') : ''}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id='mISLec_course_quiz_time'
                                    label={t('course.modals.newExam.settings.inputs.time.label')}
                                    name='title'
                                    size='small'
                                    value={timeLimit}
                                    onChange={(e) => setTimeLimit(e.target.value)}
                                    error={timeLimit && !/^\d*$/.test(timeLimit)}
                                    helperText={timeLimit && !/^\d*$/.test(timeLimit) ? t('profile.tabs.profile.basic.inputs.helper.numeric') : ''}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id='mISLec_course_quiz_attempts'
                                    label={t('course.modals.newExam.settings.inputs.attempts.label')}
                                    name='title'
                                    size='small'
                                    value={attempts}
                                    onChange={(e) => setAttempts(e.target.value)}
                                    error={attempts && !/^\d*$/.test(attempts)}
                                    helperText={attempts && !/^\d*$/.test(attempts) ? t('profile.tabs.profile.basic.inputs.helper.numeric') : ''}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </>
                }
                <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider />
                </Grid>
                <Typography variant='h6' sx={{ fontSize: '1rem !important' }}>
                    {t('course.modals.newQuiz.questions.title')}
                </Typography>
                <Typography variant='body2' sx={{ mt: 1, mb: 2 }}>
                    {t(`course.modals.${final ? 'newExam' : 'newQuiz'}.questions.description`)}
                </Typography>
                {(!questions || questions?.length === 0) && (
                    <Grid container spacing={2} sx={{ mt: { md: 3 }, mb: 3, pt: 0 }}>
                        <Grid item xs={12} md={4} sx={{ pt: { md: '0px !important' } }}>
                            <TextField
                                id='mISLec_course_quiz_no_single'
                                name='noSingle'
                                size='small'
                                label={t('course.modals.newQuiz.settings.inputs.noSingle.label')}
                                value={noSingle}
                                onChange={(e) => handleNoSingleChange(e.target.value)}
                                error={noSingleError}
                                helperText={noSingleError ? t('course.modals.newQuiz.settings.inputs.helper.numeric') : ''}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ pt: { md: '0px !important' } }}>
                            <TextField
                                id='mISLec_course_quiz_no_multiple'
                                name='noMultiple'
                                size='small'
                                label={t('course.modals.newQuiz.settings.inputs.noMultiple.label')}
                                value={noMultiple}
                                onChange={(e) => handleNoMultipleChange(e.target.value)}
                                error={noMultipleError}
                                helperText={noMultipleError ? t('course.modals.newQuiz.settings.inputs.helper.numeric') : ''}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ pt: { md: '0px !important' } }}>
                            <FormControl size='small' fullWidth>
                                <InputLabel id='mISLec_course_quiz_language_label'>{t('course.modals.newQuiz.settings.inputs.language.label')}</InputLabel>
                                <Select
                                    id='mISLec_course_quiz_language'
                                    labelId='mISLec_course_quiz_language_label'
                                    label={t('course.modals.newQuiz.settings.inputs.language.label')}
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {Object.keys(i18n.options.resources).map((lng) => (
                                        <MenuItem
                                            value={lng}
                                            key={lng}
                                        >
                                            <ReactCountryFlag
                                                countryCode={lng.split('-')[1]}
                                                svg
                                                style={{ fontSize: '1.2em', width: '1.2em', borderRadius: '4px' }}
                                            />

                                            <Typography variant='body2' sx={{ display: 'inline', color: 'text.primary', fontSize: '0.8rem', ml: 1 }}>
                                                {t('header.language.' + lng)}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {final &&
                            <Grid item xs={6}>
                                <FormControl size='small' fullWidth>
                                    <InputLabel id='mISLec_course_quiz_sections_label'>{t('course.modals.newQuiz.settings.inputs.sections.label')}</InputLabel>
                                    <Select
                                        id='mISLec_course_quiz_sections'
                                        labelId='mISLec_course_quiz_sections'
                                        label={t('course.modals.newQuiz.settings.inputs.sections.label')}
                                        multiple
                                        value={selectedSections}
                                        onChange={(e) => setSelectedSections(e.target.value)}
                                        renderValue={(selectedSection) => selectedSection.map((section) => section.title).join(', ')}
                                        MenuProps={MenuProps}
                                    >
                                        {sections?.filter((section) => section.type === 'content').map((section) => (
                                            <MenuItem key={section.id} value={section}>
                                                <Typography variant='body2'>{section.title}</Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        }
                        <Grid item xs={final ? 6 : 12}>
                            <FormControl size='small' fullWidth>
                                <InputLabel id='mISLec_course_quiz_material_label'>{t('course.modals.newQuiz.settings.inputs.material.label')}</InputLabel>
                                <Select
                                    id='mISLec_course_quiz_material'
                                    labelId='mISLec_course_quiz_material'
                                    label={t('course.modals.newQuiz.settings.inputs.material.label')}
                                    multiple
                                    value={selectedMaterial}
                                    onChange={(e) => setSelectedMaterial(e.target.value)}
                                    renderValue={(selectedMaterial) => selectedMaterial.map((material) => material.filename).join(', ')}
                                    MenuProps={MenuProps}
                                >
                                    {material?.length === 0 ?
                                        <MenuItem disabled>
                                            <Typography variant='body2'>{t('course.modals.newQuiz.settings.error.noMaterial')}</Typography>
                                        </MenuItem>
                                        :
                                        material?.map((material) => (
                                            <MenuItem key={material._id} value={material}>
                                                <Typography variant='body2'>{material.filename}</Typography>
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ pt: '0px !important' }}>
                            <FormHelperText sx={{ ml: '4px !important' }}>{t(`course.modals.newQuiz.settings.inputs.helper.${final ? 'sections' : 'material'}`)}</FormHelperText>
                        </Grid>
                    </Grid>
                )}
                {loading && (
                    <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='center'
                        sx={{
                            mt: 4,
                            mb: 4
                        }}
                    >
                        <CircularProgress size={36} />
                    </Stack>
                )}
                {questions?.length > 0 && (
                    <Stack spacing={1} sx={{ maxHeight: '40vh', overflowY: 'auto', scrollbarWidth: 'none', mb: 2 }}>
                        {questions.map((question, index) => (
                            <>
                                <Question
                                    key={question?.id ? question.id : question.temp_id}
                                    index={index}
                                    last={index === questions.length - 1}
                                    question={question}
                                    final={final}
                                    update={handleUpdate}
                                    move={moveQuestion}
                                    duplicate={duplicateQuestion}
                                    remove={removeQuestion}
                                />
                                <Grid container>
                                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                        <Tooltip title={t('course.modals.newQuiz.questions.tooltips.insertSingle')} arrow>
                                            <Grid
                                                container
                                                wrap='nowrap'
                                                alignItems='center'
                                                onClick={() => insertQuestion(index, 'singleChoice')}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    p: 0.5,
                                                    fontSize: '0.8rem',
                                                    width: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                                        borderRadius: '8px'
                                                    }
                                                }}
                                            >
                                                <Grid item sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                                    <Iconify icon='codicon:insert' sx={{ color: 'primary.main', lineHeight: 1, verticalAlign: 'middle' }} width={18} />
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {t('course.modals.newQuiz.questions.type.singleChoice')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                        <Tooltip title={t('course.modals.newQuiz.questions.tooltips.insertMultiple')} arrow>
                                            <Grid
                                                container
                                                wrap='nowrap'
                                                alignItems='center'
                                                onClick={() => insertQuestion(index, 'multipleChoice')}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    p: 0.5,
                                                    fontSize: '0.8rem',
                                                    width: 'auto',
                                                    ml: { xs: 0, md: 'auto' },
                                                    flexDirection: { md: 'row-reverse', xs: 'row' },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                                        borderRadius: '8px'
                                                    }
                                                }}
                                            >
                                                <Grid item sx={{ ml: { xs: 0, md: 1 }, mr: { xs: 1, md: 0 }, display: 'flex', alignItems: 'center', transform: { md: 'rotate(180deg)', xs: 'none' } }}>
                                                    <Iconify icon='codicon:insert' sx={{ color: 'primary.main', lineHeight: 1, verticalAlign: 'middle' }} width={18} />
                                                </Grid>
                                                <Grid item sx={{ textAlign: { md: 'right', xs: 'left' } }}>
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {t('course.modals.newQuiz.questions.type.multipleChoice')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </>
                        ))}
                    </Stack>
                )}
                <Stack
                    direction='row'
                    alignItems='center'
                    mt={4}
                    sx={{
                        justifyContent: {
                            xs: 'space-between',
                            md: 'flex-end'
                        }
                    }}
                >
                    <Button
                        variant=''
                        onClick={() => handleClose()}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.error.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'error.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'error.main',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                            }
                        }}
                    >
                        {t('course.modals.newQuiz.cancel')}
                    </Button>
                    {(!questions || questions.length === 0) && (
                        <Button
                            variant=''
                            onClick={() => generate()}
                            sx={{
                                ml: {
                                    xs: 0,
                                    md: 1
                                },
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            <Iconify icon='heroicons:sparkles-solid' width={20} sx={{ mr: 1, transform: 'scale(-1, -1)' }} />
                            {t('course.modals.newQuiz.generate')}
                        </Button>
                    )}
                    {questions && questions?.length > 0 && (
                        <Button
                            onClick={() => handleSave()}
                            sx={{
                                ml: {
                                    xs: 0,
                                    md: 1
                                },
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            {quiz ? t(`course.modals.newQuiz.update`) : t(`course.modals.${final ? 'newExam' : 'newQuiz'}.create`)}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Modal>
    );
}

export default QuizModal;